<script setup>
import ListFetcher from '@/features/data/presentation/components/ListFetcher.vue'
import PaginationUi from './PaginationUi.vue'
import ColorImg from '@/presentation/components/img/ColorImg.vue'
import { computed, ref, watch } from 'vue'
import ImgComponent from '@/presentation/components/img/ImgComponent.vue'
import InputComponent from '@/presentation/components/input/InputComponent.vue'

const emit = defineEmits(['update:filters', 'action'])

const search = ref('')
const sortKey = ref('none')
const sortOrder = ref('desc')
const colFilters = ref({})

const fetcherRef = ref(null)

watch(
  [search, sortKey, sortOrder, colFilters],
  () => {
    emit('update:filters', {
      search: search.value,
      sortKey: sortKey.value,
      sortOrder: sortOrder.value,
      ...colFilters.value,
    })

    if (fetcherRef.value && typeof fetcherRef.value.changePage === 'function') {
      fetcherRef.value.changePage(0)
    }
  },
  { deep: true, immediate: true },
)

const hasSelectedCol = computed(() => {
  return Object.values(colFilters.value).filter(el => !!el).length > 0
})

const actionCol = computed(() => {
  return Array.isArray(props.cols)
    ? props.cols.find(col => col.id === 'action' && col.onClick)
    : null
})

const actionColIcon = computed(() => {
  return actionCol.value?.icon
})

const filterableCols = computed(() => {
  return Array.isArray(props.cols)
    ? props.cols.filter(
        col => col.filter?.key && Array.isArray(col.filter?.options),
      )
    : []
})

const hasFilterableCols = computed(() => {
  return filterableCols.value.length > 0
})

const sortableCols = computed(() => {
  return Array.isArray(props.cols) ? props.cols.filter(col => col.sortKey) : []
})

const hasSortableCols = computed(() => {
  return sortableCols.value.length > 0
})

const props = defineProps({
  title: String,
  dataKey: { type: String, required: false },
  items: { type: Array, default: null },
  isLoading: { type: Boolean, default: false },
  hasSearch: { type: Boolean, default: false },
  hidePagination: { type: Boolean, default: false },
  buttons: {
    type: Array,
  },
  cols: {
    type: Array,
    required: true,
  },
  cardConfig: {
    type: Object,
    default: () => ({
      imageField: null,
      titleField: 'name',
      subtitleField: null,
      statusField: null,
      priceField: null,
      descriptionField: null,
      extraFields: [],
    }),
  },
  gridCols: {
    type: Object,
    default: () => ({
      sm: 1,
      md: 2,
      lg: 3,
      xl: 4,
    }),
  },
  refreshOnEvents: { type: [String, Array] },
  refreshDelay: { type: Number },
  limit: { type: [Number, String], default: 10 },
  elClickHanler: { type: Function, default: () => {} },
  defaultFilters: { type: Object, default: () => ({}) },
})

function getInitials(str) {
  if (typeof str !== 'string') return str
  if (!str) return ''
  const words = str.trim().split(/\s+/)
  if (words.length === 1) {
    const word = words[0]
    if (word.length === 1) return word[0].toUpperCase()
    return (word[0] + word[word.length - 1]).toUpperCase()
  }
  return words
    .map(w => w[0].toUpperCase())
    .join('')
    .slice(0, 2)
}

function handleSort(col) {
  if (!col.sortKey) return
  if (sortKey.value === col.sortKey) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = col.sortKey
    sortOrder.value = 'asc'
  }
}

function clearFilters() {
  Object.keys(colFilters.value).forEach(key => {
    colFilters.value[key] = ''
  })
}

function getStatusColor(status) {
  const statusColors = {
    validated: {
      bg: 'bg-green-100',
      text: 'text-green-700',
      border: 'border-green-200',
    },
    pending: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-700',
      border: 'border-yellow-200',
    },
    rejected: {
      bg: 'bg-red-100',
      text: 'text-red-700',
      border: 'border-red-200',
    },
    active: {
      bg: 'bg-green-100',
      text: 'text-green-700',
      border: 'border-green-200',
    },
    inactive: {
      bg: 'bg-gray-100',
      text: 'text-gray-700',
      border: 'border-gray-200',
    },
    delivered: {
      bg: 'bg-green-100',
      text: 'text-green-700',
      border: 'border-green-200',
    },
    processing: {
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      border: 'border-blue-200',
    },
    cancelled: {
      bg: 'bg-red-100',
      text: 'text-red-700',
      border: 'border-red-200',
    },
  }
  const colors = statusColors[status?.toLowerCase?.()] || {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-200',
  }
  return `${colors.bg} ${colors.text} ${colors.border}`
}

function formatPrice(price) {
  if (!price) return '0 FCFA'
  return `${price.toLocaleString()} FCFA`
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function getCardValue(item, field) {
  if (!field || typeof field !== 'string') return null
  const value = item[field]

  if (field.includes('.') && value === undefined) {
    const nestedValue = field.split('.').reduce((obj, key) => obj?.[key], item)
    return nestedValue
  }

  return value
}
</script>

<template>
  <div class="overflow-auto rounded-2xl border border-gray-200 bg-white pt-4">
    <!-- HEADER: titre + recherche -->
    <div
      class="flex flex-col gap-5 px-6 mb-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <h3 class="text-xl font-semibold text-gray-800">{{ title }}</h3>
      </div>
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          v-for="(button, i) in buttons"
          :key="i"
          @click="button.onClick"
          class="buttonComponent whitespace-nowrap"
        >
          <span v-html="button.title"></span>
        </button>
        <div class="relative" v-if="hasSearch">
          <button class="absolute -translate-y-1/2 left-4 top-1/2">
            <svg
              class="fill-gray-500"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M3.04199 9.37381C3.04199 5.87712 5.87735 3.04218 9.37533 3.04218C12.8733 3.04218 15.7087 5.87712 15.7087 9.37381C15.7087 12.8705 12.8733 15.7055 9.37533 15.7055C5.87735 15.7055 3.04199 12.8705 3.04199 9.37381ZM9.37533 1.54218C5.04926 1.54218 1.54199 5.04835 1.54199 9.37381C1.54199 13.6993 5.04926 17.2055 9.37533 17.2055C11.2676 17.2055 13.0032 16.5346 14.3572 15.4178L17.1773 18.2381C17.4702 18.531 17.945 18.5311 18.2379 18.2382C18.5308 17.9453 18.5309 17.4704 18.238 17.1775L15.4182 14.3575C16.5367 13.0035 17.2087 11.2671 17.2087 9.37381C17.2087 5.04835 13.7014 1.54218 9.37533 1.54218Z"
              />
            </svg>
          </button>
          <input
            type="text"
            v-model="search"
            placeholder="Rechercher..."
            class="h-11 w-full rounded-lg border border-gray-300 bg-transparent py-2.5 pl-[42px] pr-4 text-base text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-primary-300 focus:outline-hidden focus:ring-3 focus:ring-primary-500/10 xl:w-[300px]"
          />
        </div>
      </div>
    </div>

    <!-- OPTIONS DE TRI ET FILTRE -->
    <div v-if="hasFilterableCols || hasSortableCols" class="px-6 pb-4">
      <div class="flex flex-col lg:flex-row lg:items-center gap-4">
        <!-- Filtres -->
        <div v-if="hasFilterableCols" class="flex flex-wrap gap-3 items-center">
          <span class="text-sm text-gray-700 font-medium">Filtres:</span>
          <div
            v-for="col in filterableCols"
            :key="col.filter?.key"
            class="relative"
          >
            <InputComponent
              type="select"
              :options="col.filter?.options"
              :formatOptionsDisplay="
                el => {
                  let value = `<div class='flex justify-between items-center gap-5'> <span>${el?.name ?? '-'}</span>  `
                  if (colFilters[col.filter.key] == el.value)
                    value += `<div class='w-2 h-2 bg-primary rounded-full'></div>`
                  value += `</div>`
                  return value
                }
              "
              @input="
                el => {
                  colFilters[col.filter.key] = el.value
                }
              "
              v-slot="{ data: slotData } = { data: null }"
            >
              <div
                class="p-1 rounded ripple cursor-pointer"
                @mouseover="
                  () => {
                    slotData?.show()
                  }
                "
              >
                <ColorImg
                  src="/images/icons/filter.svg"
                  class="bg-current w-4 h-4"
                />
                <div
                  class="absolute rounded-full w-2 h-2 bg-primary top-0 right-0"
                  v-if="!!colFilters[col.filter.key]"
                ></div>
              </div>
            </InputComponent>
          </div>

          <button
            v-if="hasSelectedCol"
            @click="clearFilters"
            class="px-3 py-1.5 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Effacer les filtres
          </button>
        </div>

        <!-- Tri -->
        <div v-if="hasSortableCols" class="flex flex-wrap gap-3 items-center">
          <span class="text-sm text-gray-700 font-medium">Trier par:</span>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="col in sortableCols"
              :key="col.sortKey"
              @click="handleSort(col)"
              :class="[
                'px-3 py-1.5 text-sm rounded-lg transition-colors',
                sortKey === col.sortKey
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
              ]"
            >
              {{ col.name || col.sortKey }}
              <span v-if="sortKey === col.sortKey" class="ml-1">
                {{ sortOrder === 'asc' ? '↑' : '↓' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- LIST FETCHER + GRID -->
    <div class="min-w-full overflow-x-auto custom-scrollbar px-6 pb-4">
      <!-- Use items directly if provided, otherwise use ListFetcher -->
      <template v-if="items">
        <div
          class="grid gap-4"
          :class="[
            `grid-cols-${gridCols.sm}`,
            `md:grid-cols-${gridCols.md}`,
            `lg:grid-cols-${gridCols.lg}`,
            `xl:grid-cols-${gridCols.xl}`,
          ]"
        >
          <div
            v-for="(item, i) in items"
            :key="i"
            class="group relative rounded-2xl border border-gray-200 bg-white p-4 hover:shadow-theme-sm transition-all duration-200 hover:border-primary-200 overflow-hidden"
            :class="{ shimmerContainer: isLoading }"
            @click="elClickHanler?.(item)"
          >
            <!-- Card Content (same as below) -->
            <div class="flex flex-col h-full">
              <!-- Header with Image -->
              <div class="flex items-start justify-between mb-3">
                <div class="flex items-center space-x-3 flex-1">
                  <div
                    class="flex items-center justify-center w-12 h-12 rounded-xl overflow-hidden border border-primary-50 bg-primary-100 flex-shrink-0"
                    :class="{ shimmerTarget: isLoading }"
                  >
                    <div
                      class="w-full h-full"
                      v-if="
                        cardConfig.imageField &&
                        getCardValue(item, cardConfig.imageField)
                      "
                    >
                      <ImgComponent
                        class="w-full h-full object-cover bg-white"
                        fallback="/images/logo.png"
                        :src="getCardValue(item, cardConfig.imageField)"
                      />
                    </div>
                    <span
                      v-else
                      class="text-sm font-semibold text-primary-500"
                      :class="{ shimmerTarget: isLoading }"
                    >
                      {{
                        getInitials(
                          getCardValue(item, cardConfig.titleField) || 'N/A',
                        )
                      }}
                    </span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h4
                      class="font-semibold text-gray-800 text-base truncate"
                      :class="{ shimmerTarget: isLoading }"
                    >
                      {{
                        cardConfig.titleFormatter
                          ? cardConfig.titleFormatter(
                              getCardValue(item, cardConfig.titleField),
                              item,
                            )
                          : getCardValue(item, cardConfig.titleField) ||
                            'Sans nom'
                      }}
                    </h4>
                    <p
                      v-if="
                        isLoading ||
                        (cardConfig.subtitleField &&
                          getCardValue(item, cardConfig.subtitleField))
                      "
                      class="text-gray-500 text-sm truncate"
                      :class="{ shimmerTarget: isLoading }"
                    >
                      {{
                        getCardValue(item, cardConfig.subtitleField) ||
                        (isLoading ? '••••••••••••' : '')
                      }}
                    </p>
                  </div>
                </div>

                <!-- Status Badge -->
                <div
                  v-if="
                    isLoading ||
                    (cardConfig.statusField &&
                      getCardValue(item, cardConfig.statusField))
                  "
                  class="flex-shrink-0 ml-2"
                >
                  <span
                    class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium shimmerTarget"
                    :class="
                      isLoading
                        ? 'bg-gray-200 text-transparent'
                        : getStatusColor(
                            getCardValue(
                              item,
                              cardConfig.statusIdField ||
                                cardConfig.statusField,
                            ),
                          )
                    "
                  >
                    {{
                      getCardValue(item, cardConfig.statusField) ||
                      (isLoading ? '•••' : '')
                    }}
                  </span>
                </div>
              </div>
              <!-- Description -->
              <p
                v-if="
                  isLoading ||
                  (cardConfig.descriptionField &&
                    getCardValue(item, cardConfig.descriptionField))
                "
                class="text-gray-600 text-sm mb-3 line-clamp-2"
                :class="{ shimmerTarget: isLoading }"
              >
                {{
                  getCardValue(item, cardConfig.descriptionField) ||
                  (isLoading
                    ? '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••'
                    : '')
                }}
              </p>

              <!-- Extra Fields -->
              <div
                v-if="
                  cardConfig.extraFields && cardConfig.extraFields.length > 0
                "
                class="space-y-2 mb-3 flex-1"
              >
                <div
                  v-for="field in cardConfig.extraFields"
                  :key="field.key"
                  class="flex items-center justify-between"
                >
                  <span class="text-gray-500 text-sm">{{ field.label }}:</span>
                  <span
                    class="text-sm font-medium"
                    :class="[
                      field.highlight
                        ? 'text-orange-600 font-semibold'
                        : 'text-gray-800',
                      { shimmerTarget: isLoading },
                    ]"
                  >
                    {{
                      isLoading
                        ? '••••••••••••'
                        : field.formatter
                          ? field.formatter(getCardValue(item, field.key), item)
                          : getCardValue(item, field.key)
                    }}
                  </span>
                </div>
              </div>

              <!-- Price -->
              <div
                v-if="
                  isLoading ||
                  (cardConfig.priceField &&
                    getCardValue(item, cardConfig.priceField))
                "
                class="mb-3"
              >
                <span class="text-lg font-bold text-primary-600 shimmerTarget">
                  {{
                    isLoading
                      ? '••••••'
                      : formatPrice(getCardValue(item, cardConfig.priceField))
                  }}
                </span>
              </div>

              <!-- Footer Actions -->
              <div
                class="flex items-center justify-between pt-3 border-t border-gray-100"
              >
                <div class="flex items-center space-x-1">
                  <!-- Primary Action -->
                  <button
                    v-if="actionCol"
                    @click="actionCol.onClick(item)"
                    class="p-2.5 rounded-lg hover:bg-gray-100 transition-colors ripple"
                    title="Voir les détails"
                  >
                    <ColorImg
                      v-if="actionColIcon"
                      :src="actionColIcon(item)"
                      class="bg-gray-600 w-4 h-4"
                    />
                    <svg
                      v-else
                      class="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  </button>
                </div>

                <!-- Quick Stats -->
                <div class="flex items-center space-x-3 text-sm text-gray-500">
                  <!-- Additional quick info can be added here -->
                </div>
              </div>
            </div>

            <!-- Shimmer overlay for loading state -->
            <div
              v-if="isLoading"
              class="absolute inset-0 bg-white/50 shimmer-element"
            ></div>
          </div>
        </div>
      </template>

      <ListFetcher
        v-else
        ref="fetcherRef"
        :dataKey="dataKey"
        :limit="limit"
        :refreshOnEvents="refreshOnEvents"
        :refreshDelay="refreshDelay"
        :filters="{ search, sortKey, sortOrder, ...colFilters, ...props.defaultFilters }"
      >
        <template #loadingAndItems="{ items, limit, loading, result }">
          <div
            class="grid gap-4"
            :class="[
              `grid-cols-${gridCols.sm}`,
              `md:grid-cols-${gridCols.md}`,
              `lg:grid-cols-${gridCols.lg}`,
              `xl:grid-cols-${gridCols.xl}`,
            ]"
          >
            <div
              v-for="(item, i) in [
                ...(items || []),
                ...(loading
                  ? [
                      ...items,
                      ...items,
                      ...items,
                      ...items,
                      ...items,
                      ...items,
                      ...items,
                      ...items,
                    ]
                  : []),
                ...(loading ? new Array(8).fill({}) : []),
              ].slice(0, loading ? limit : 10000)"
              :key="i"
              class="group relative rounded-2xl border border-gray-200 bg-white p-4 hover:shadow-theme-sm transition-all duration-200 hover:border-primary-200 overflow-hidden"
              :class="{ shimmerContainer: loading }"
              @click="elClickHanler?.(item)"
            >
              <!-- Card Content -->
              <div class="flex flex-col h-full">
                <!-- Header with Image -->
                <div class="flex items-start justify-between mb-3">
                  <div class="flex items-center space-x-3 flex-1">
                    <div
                      class="flex items-center justify-center w-12 h-12 rounded-xl overflow-hidden border border-primary-50 bg-primary-100 shimmerTarget flex-shrink-0"
                    >
                      <div
                        class="w-full h-full"
                        v-if="
                          cardConfig.imageField &&
                          getCardValue(item, cardConfig.imageField)
                        "
                      >
                        <ImgComponent
                          class="w-full h-full object-cover bg-white"
                          fallback="/images/logo.png"
                          :src="getCardValue(item, cardConfig.imageField)"
                        />
                      </div>
                      <span
                        v-else
                        class="text-sm font-semibold text-primary-500 shimmerTarget"
                      >
                        {{
                          getInitials(
                            getCardValue(item, cardConfig.titleField) || 'N/A',
                          )
                        }}
                      </span>
                    </div>
                    <div class="flex-1 min-w-0">
                      <h4
                        class="font-semibold text-gray-800 text-base truncate shimmerTarget"
                      >
                        {{
                          cardConfig.titleFormatter
                            ? cardConfig.titleFormatter(
                                getCardValue(item, cardConfig.titleField),
                                item,
                              )
                            : getCardValue(item, cardConfig.titleField) ||
                              'Sans nom'
                        }}
                      </h4>
                      <p
                        v-if="
                          cardConfig.subtitleField &&
                          getCardValue(item, cardConfig.subtitleField)
                        "
                        class="text-gray-500 text-sm truncate shimmerTarget"
                      >
                        {{ getCardValue(item, cardConfig.subtitleField) }}
                      </p>
                    </div>
                  </div>

                  <!-- Status Badge -->
                  <div
                    v-if="
                      cardConfig.statusField &&
                      getCardValue(item, cardConfig.statusField)
                    "
                    class="flex-shrink-0 ml-2"
                  >
                    <span
                      class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium shimmerTarget"
                      :class="
                        getStatusColor(
                          getCardValue(
                            item,
                            cardConfig.statusIdField || cardConfig.statusField,
                          ),
                        )
                      "
                    >
                      {{ getCardValue(item, cardConfig.statusField) }}
                    </span>
                  </div>
                </div>

                <!-- Description -->
                <p
                  v-if="
                    cardConfig.descriptionField &&
                    getCardValue(item, cardConfig.descriptionField)
                  "
                  class="text-gray-600 text-sm mb-3 line-clamp-2 shimmerTarget"
                >
                  {{ getCardValue(item, cardConfig.descriptionField) }}
                </p>

                <!-- Extra Fields -->
                <div
                  v-if="
                    cardConfig.extraFields && cardConfig.extraFields.length > 0
                  "
                  class="space-y-2 mb-3 flex-1"
                >
                  <div
                    v-for="field in cardConfig.extraFields"
                    :key="field.key"
                    class="flex items-center justify-between"
                  >
                    <span class="text-gray-500 text-sm"
                      >{{ field.label }}:</span
                    >
                    <span
                      class="text-sm font-medium shimmerTarget"
                      :class="[
                        field.highlight
                          ? 'text-orange-600 font-semibold'
                          : 'text-gray-800',
                      ]"
                    >
                      {{
                        field.formatter
                          ? field.formatter(getCardValue(item, field.key), item)
                          : getCardValue(item, field.key)
                      }}
                    </span>
                  </div>
                </div>

                <!-- Price -->
                <div
                  v-if="
                    cardConfig.priceField &&
                    getCardValue(item, cardConfig.priceField)
                  "
                  class="mb-3"
                >
                  <span
                    class="text-lg font-bold text-primary-600 shimmerTarget"
                  >
                    {{ formatPrice(getCardValue(item, cardConfig.priceField)) }}
                  </span>
                </div>

                <!-- Footer Actions -->
                <div
                  class="flex items-center justify-between pt-3 border-t border-gray-100"
                >
                  <div class="flex items-center space-x-1">
                    <!-- Primary Action -->
                    <button
                      v-if="actionCol"
                      @click="actionCol.onClick(item)"
                      class="p-2.5 rounded-lg hover:bg-gray-100 transition-colors ripple"
                      title="Voir les détails"
                    >
                      <ColorImg
                        v-if="actionColIcon"
                        :src="actionColIcon(item)"
                        class="bg-gray-600 w-4 h-4"
                      />
                      <svg
                        v-else
                        class="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>
                  </div>

                  <!-- Quick Stats -->
                  <div
                    class="flex items-center space-x-3 text-sm text-gray-500"
                  >
                    <!-- Additional quick info can be added here -->
                  </div>
                </div>
              </div>

              <!-- Shimmer overlay for loading state -->
              <div
                v-if="loading"
                class="absolute inset-0 bg-white/50 shimmer-element"
              ></div>
            </div>
          </div>
        </template>

        <!-- PAGINATION -->
        <template
          #pagination="{
            page,
            totalPages,
            limit,
            total,
            changePage,
            changeLimit,
            nextPage,
            prevPage,
          }"
        >
          <div v-if="hidePagination" class="hidden"></div>
          <PaginationUi
            v-else
            :page="page"
            :totalPages="totalPages"
            :limit="limit"
            :total="total"
            :changePage="changePage"
            :changeLimit="changeLimit"
            :nextPage="nextPage"
            :prevPage="prevPage"
          />
        </template>
      </ListFetcher>
    </div>
  </div>
</template>

<style scoped>
/* Line clamp utility */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Grid utilities */
.grid-cols-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.grid-cols-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.grid-cols-4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

@media (min-width: 640px) {
  .sm\:grid-cols-1 {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
  .sm\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .sm\:grid-cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .lg\:grid-cols-1 {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
  .lg\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .lg\:grid-cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .lg\:grid-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .xl\:grid-cols-1 {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
  .xl\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .xl\:grid-cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .xl\:grid-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

/* Custom scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
